/**
 * Lazy-eye / amblyopia risk model.
 *
 * Thresholds and gating rules are derived from published screening guidance
 * and calibrated against an internal screening dataset (Brockton, May 2026):
 *
 *   - AAPOS Vision Screening Guidelines (Donahue & Baker, Pediatrics 2016)
 *   - AAO Preferred Practice Pattern — Amblyopia (2022)
 *   - PEDIG ATS protocols — interocular logMAR cutoff (Holmes et al.)
 *   - Sheard & Percival criteria for binocular phoria limits
 *   - AAO BCSC Section 6 — Pediatric Ophthalmology & Strabismus
 *
 * Amblyopia is fundamentally an *interocular* visual-acuity disorder.
 * The interocular VA difference is therefore the decisive signal: a
 * "high risk" badge fires only when VA evidence supports it, or when the
 * cover test reveals a manifest deviation in the strabismic range. Phoria
 * magnitude and fixation instability on their own are *risk factors* —
 * they raise the badge to "moderate" (worth monitoring) but cannot reach
 * "high" without VA or manifest-deviation corroboration.
 */
import { ObjectivePhoriaConditionSummary, ObjectivePhoriaResult } from "../../../types/testTypes";
import { VaStatus } from "@utils/test-specific-utils/VisualAcuityUtils";
import { isHorizontalReliable, isVerticalReliable } from "./phoriaCalculations";
import { RiskLevel, bandedSeverity, severityToLevel, severityToPercentage } from "./severity";

// ─── Age bands ───────────────────────────────────────────────────────────────

export type AgeBand = "infant" | "preschool" | "school" | "teen" | "adult";

export function getAgeBand(age: number | null | undefined): AgeBand {
  if (typeof age !== "number" || !Number.isFinite(age) || age < 0) return "adult";
  if (age < 3) return "infant";
  if (age < 6) return "preschool";
  if (age < 13) return "school";
  if (age < 18) return "teen";
  return "adult";
}

export function ageBandLabel(band: AgeBand): string {
  switch (band) {
    case "infant":
      return "infant";
    case "preschool":
      return "preschool-aged child";
    case "school":
      return "school-aged child";
    case "teen":
      return "adolescent";
    case "adult":
      return "adult";
  }
}

// "Critical period" — the developmental window where amblyopia is most
// plastic. Used to tighten messaging and to lift moderate-band deviations.
export function isCriticalPeriod(band: AgeBand): boolean {
  return band === "infant" || band === "preschool" || band === "school";
}

// ─── Visual acuity (monocular, uncorrected logMAR) ───────────────────────────

// Device measurement floor: the UI caps displayed acuity at "20/40+" and
// does not test finer letter sizes, so every measured logMAR is clamped to
// this floor before any risk calculation. Reasoning about a finer
// interocular gap than the device can actually resolve would produce
// findings the chart can't substantiate.
export const DEVICE_VA_FLOOR_LOGMAR = 0.3;

export function clampVaLogMarToFloor(logMar: number | null | undefined): number | null {
  if (typeof logMar !== "number" || !Number.isFinite(logMar)) return null;
  return Math.max(logMar, DEVICE_VA_FLOOR_LOGMAR);
}

// AAPOS preschool screening referral thresholds + adult clinical norms,
// adjusted so 20/40+ (the device floor) reads as "normal" for every age.
export const VA_LOGMAR_BORDERLINE: Record<AgeBand, number> = {
  infant: 0.5, // > 20/63
  preschool: 0.4, // > 20/50 (AAPOS 3–5y referral baseline)
  school: 0.3, // > 20/40 (device floor)
  teen: 0.3,
  adult: 0.4,
};

export const VA_LOGMAR_ATTENTION: Record<AgeBand, number> = {
  infant: 0.7,
  preschool: 0.5,
  school: 0.5,
  teen: 0.5,
  adult: 0.6,
};

export function vaStatusForAge(logMar: number | null | undefined, band: AgeBand): VaStatus | null {
  const clamped = clampVaLogMarToFloor(logMar);
  if (clamped === null) return null;
  if (clamped <= VA_LOGMAR_BORDERLINE[band]) return VaStatus.Normal;
  if (clamped <= VA_LOGMAR_ATTENTION[band]) return VaStatus.Borderline;
  return VaStatus.Attention;
}

export function vaSeverityForAge(logMar: number | null | undefined, band: AgeBand): number {
  const clamped = clampVaLogMarToFloor(logMar);
  if (clamped === null) return 0;
  const borderline = VA_LOGMAR_BORDERLINE[band];
  const attention = VA_LOGMAR_ATTENTION[band];
  // "Normal" must score zero — anyone who passed the device's 20/40+
  // screen should not see a partially-filled risk ring.
  if (clamped <= borderline) return 0;
  if (clamped <= attention) {
    const t = (clamped - borderline) / Math.max(attention - borderline, 0.001);
    return 0.34 + t * (0.65 - 0.34);
  }
  const overshoot = Math.min((clamped - attention) / Math.max(attention - borderline, 0.1), 1);
  return 0.7 + overshoot * 0.3;
}

// ─── Interocular VA difference ───────────────────────────────────────────────

// PEDIG ATS: ≥ 0.2 logMAR (≥ 2 Snellen lines) is the canonical amblyopia
// diagnostic threshold. Brockton screening data showed p90 = 0.2 logMAR,
// confirming this as the outlier boundary. Both eyes are clamped to the
// device floor first — if both report 20/40+, the resolvable difference is
// zero by definition.
export const VA_INTEROCULAR_MODERATE = 0.1; // 1 line — suspicion
export const VA_INTEROCULAR_HIGH = 0.2; // 2 lines — PEDIG amblyopia criterion

function vaInterocularSeverity(
  logMar1: number | null | undefined,
  logMar2: number | null | undefined,
): { diffLogMar: number | null; severity: number; level: RiskLevel } {
  const a = clampVaLogMarToFloor(logMar1);
  const b = clampVaLogMarToFloor(logMar2);
  if (a === null || b === null) {
    return { diffLogMar: null, severity: 0, level: "low" };
  }
  const diff = Math.abs(a - b);
  const severity = bandedSeverity(diff, VA_INTEROCULAR_MODERATE, VA_INTEROCULAR_HIGH);
  return { diffLogMar: diff, severity, level: severityToLevel(severity) };
}

export function vaInterocularLines(diffLogMar: number): number {
  // logMAR step per Snellen line ≈ 0.1
  return Math.round(diffLogMar * 10);
}

// ─── Cover test (manifest deviation under cover) ─────────────────────────────

// Device emits either gaze angles in degrees or normalized screen units.
// 4° displacement ≈ 7 PD ≈ AAPOS strabismus referral magnitude.
// Brockton screening export contained no cover-test records, so these
// remain expert-set from AAPOS strabismus referral guidance.
export const COVER_THRESHOLDS_DEG = {
  moderateMean: 2.0,
  highMean: 4.0,
  moderateStability: 1.5,
  highStability: 3.0,
} as const;

export const COVER_THRESHOLDS_NORM = {
  moderateMean: 0.2,
  highMean: 0.4,
  moderateStability: 0.15,
  highStability: 0.3,
} as const;

export function coverThresholds(isDegrees: boolean) {
  return isDegrees ? COVER_THRESHOLDS_DEG : COVER_THRESHOLDS_NORM;
}

export function coverTestSeverity(
  meanOffset: number,
  stability: number,
  isDegrees: boolean,
): { meanSeverity: number; stabilitySeverity: number; combined: number } {
  const t = coverThresholds(isDegrees);
  const meanSeverity = Number.isFinite(meanOffset)
    ? bandedSeverity(Math.abs(meanOffset), t.moderateMean, t.highMean)
    : 0;
  const stabilitySeverity = Number.isFinite(stability)
    ? bandedSeverity(Math.abs(stability), t.moderateStability, t.highStability)
    : 0;
  return { meanSeverity, stabilitySeverity, combined: Math.max(meanSeverity, stabilitySeverity) };
}

export function describeFixationStability(stability: number, isDegrees: boolean): string {
  if (!Number.isFinite(stability)) return "N/A";
  const t = coverThresholds(isDegrees);
  if (Math.abs(stability) <= t.moderateStability) return "Stable";
  if (Math.abs(stability) <= t.highStability) return "Variable";
  return "Unstable";
}

// ─── Objective phoria (PD) ───────────────────────────────────────────────────

// Distance: > 4 PD eso or > 8 PD exo is outside the comfortable fusional
// reserve (Sheard). Near: physiological exophoria up to ~6 PD is normal,
// so the moderate band starts higher. Vertical: > 2 PD is clinically
// notable on any condition.
// Tightened esophoria-at-distance thresholds for kids reflect that
// accommodative esotropia is the most common pediatric strabismus.
export const PHORIA_H_DISTANCE_MODERATE = 4;
export const PHORIA_H_DISTANCE_HIGH = 8;
export const PHORIA_H_NEAR_MODERATE = 6;
export const PHORIA_H_NEAR_HIGH = 10;
export const PHORIA_V_MODERATE = 2;
export const PHORIA_V_HIGH = 4;
export const PHORIA_H_DISTANCE_ESO_MODERATE_KIDS = 3;
export const PHORIA_H_DISTANCE_ESO_HIGH_KIDS = 6;
export const PHORIA_NEAR_DISTANCE_SHIFT_MODERATE = 4;
export const PHORIA_NEAR_DISTANCE_SHIFT_HIGH = 8;

function isDistanceCondition(name: string | undefined | null): boolean {
  return (name ?? "").toLowerCase() === "distance";
}
function isNearCondition(name: string | undefined | null): boolean {
  return (name ?? "").toLowerCase() === "near";
}

function horizontalPhoriaSeverity(
  summary: ObjectivePhoriaConditionSummary,
  band: AgeBand,
  occlusions: ObjectivePhoriaResult["occlusions"] | undefined,
): number {
  if (!isHorizontalReliable(summary, occlusions)) return 0;
  const h = Number(summary.horizontalPhoriaPd);
  if (!Number.isFinite(h)) return 0;
  const distance = isDistanceCondition(summary.conditionName);
  const near = isNearCondition(summary.conditionName);
  if (distance) {
    // Esophoria-at-distance is the most concerning pattern in children.
    const isEso = h > 0;
    if (isEso && isCriticalPeriod(band)) {
      return bandedSeverity(
        Math.abs(h),
        PHORIA_H_DISTANCE_ESO_MODERATE_KIDS,
        PHORIA_H_DISTANCE_ESO_HIGH_KIDS,
      );
    }
    return bandedSeverity(Math.abs(h), PHORIA_H_DISTANCE_MODERATE, PHORIA_H_DISTANCE_HIGH);
  }
  if (near) {
    return bandedSeverity(Math.abs(h), PHORIA_H_NEAR_MODERATE, PHORIA_H_NEAR_HIGH);
  }
  // Unknown condition — treat as distance.
  return bandedSeverity(Math.abs(h), PHORIA_H_DISTANCE_MODERATE, PHORIA_H_DISTANCE_HIGH);
}

function verticalPhoriaSeverity(
  summary: ObjectivePhoriaConditionSummary,
  occlusions: ObjectivePhoriaResult["occlusions"] | undefined,
): number {
  if (!isVerticalReliable(summary, occlusions)) return 0;
  const v = Number(summary.verticalPhoriaPd);
  if (!Number.isFinite(v)) return 0;
  return bandedSeverity(Math.abs(v), PHORIA_V_MODERATE, PHORIA_V_HIGH);
}

function nearDistanceShiftSeverity(
  distance: ObjectivePhoriaConditionSummary | undefined,
  near: ObjectivePhoriaConditionSummary | undefined,
  occlusions: ObjectivePhoriaResult["occlusions"] | undefined,
): number {
  if (!distance || !near) return 0;
  if (!isHorizontalReliable(distance, occlusions) || !isHorizontalReliable(near, occlusions))
    return 0;
  const d = Number(distance.horizontalPhoriaPd);
  const n = Number(near.horizontalPhoriaPd);
  if (!Number.isFinite(d) || !Number.isFinite(n)) return 0;
  const shift = Math.abs(n - d);
  return bandedSeverity(
    shift,
    PHORIA_NEAR_DISTANCE_SHIFT_MODERATE,
    PHORIA_NEAR_DISTANCE_SHIFT_HIGH,
  );
}

function phoriaSeverity(data: ObjectivePhoriaResult, band: AgeBand): number {
  const summaries = data.conditionSummaries ?? [];
  const occlusions = data.occlusions;
  const horizontalSev = summaries.reduce(
    (acc, s) => Math.max(acc, horizontalPhoriaSeverity(s, band, occlusions)),
    0,
  );
  const verticalSev = summaries.reduce(
    (acc, s) => Math.max(acc, verticalPhoriaSeverity(s, occlusions)),
    0,
  );
  const distance = summaries.find((s) => isDistanceCondition(s.conditionName));
  const near = summaries.find((s) => isNearCondition(s.conditionName));
  const shiftSev = nearDistanceShiftSeverity(distance, near, occlusions);
  const base = Math.max(horizontalSev, verticalSev, shiftSev);
  // Quality "caution" means the measurement itself is unreliable — keep
  // severity inside the moderate band rather than claiming high risk
  // from shaky data.
  const quality = (data.overallQualityFlag ?? "").toLowerCase();
  return quality === "caution" ? Math.min(base, 0.65) : base;
}

// ─── Combined risk gating ────────────────────────────────────────────────────

export interface RiskFinding {
  level: RiskLevel;
  severity: number;
}

function toFinding(severity: number): RiskFinding {
  return { level: severityToLevel(severity), severity };
}

export function vaInterocularFinding(
  logMar1: number | null | undefined,
  logMar2: number | null | undefined,
): RiskFinding | null {
  const r = vaInterocularSeverity(logMar1, logMar2);
  if (r.diffLogMar === null) return null;
  return { level: r.level, severity: r.severity };
}

export function coverTestFinding(
  meanOffset: number,
  stability: number,
  isDegrees: boolean,
): RiskFinding | null {
  if (!Number.isFinite(meanOffset) && !Number.isFinite(stability)) return null;
  return toFinding(coverTestSeverity(meanOffset, stability, isDegrees).combined);
}

export function phoriaFinding(data: ObjectivePhoriaResult, band: AgeBand): RiskFinding {
  return toFinding(phoriaSeverity(data, band));
}

export function vaMonocularFinding(
  logMar: number | null | undefined,
  band: AgeBand,
): RiskFinding | null {
  if (typeof logMar !== "number" || !Number.isFinite(logMar)) return null;
  return toFinding(vaSeverityForAge(logMar, band));
}

export type LazyEyeRiskDriver = "va-interocular" | "cover-test" | "phoria" | "monocular-va" | null;

export interface CombinedLazyEyeRisk {
  level: RiskLevel;
  percentage: number;
  /** Which sub-finding drove the badge. */
  driver: LazyEyeRiskDriver;
  /** True when interocular VA evidence reached attention band — the
   *  canonical amblyopia signal. */
  vaSupportsAmblyopia: boolean;
}

/**
 * Combine constituent findings into a single overview risk band.
 *
 * Gating:
 *   - "high" only when:
 *       (a) interocular VA ≥ 0.2 logMAR (PEDIG amblyopia criterion), OR
 *       (b) cover test shows manifest deviation in the high band, OR
 *       (c) interocular VA ≥ 0.1 logMAR AND any supporting sign
 *           (cover or phoria) is in moderate band or higher.
 *   - Otherwise: worst of the constituent levels (capped at "moderate").
 *
 * Critical-period override: in preschool/school children, any deviation
 * finding (cover or phoria) at moderate band lifts the overall percentage
 * to the top of moderate (66) without changing the level — early
 * findings deserve full visibility.
 */
export function combineLazyEyeRisk(
  inputs: {
    vaInteroc: RiskFinding | null;
    coverTest: RiskFinding | null;
    phoria: RiskFinding | null;
    monocularVa: RiskFinding | null;
  },
  band: AgeBand,
): CombinedLazyEyeRisk | null {
  const present = [
    { key: "va-interocular" as const, finding: inputs.vaInteroc },
    { key: "cover-test" as const, finding: inputs.coverTest },
    { key: "phoria" as const, finding: inputs.phoria },
    { key: "monocular-va" as const, finding: inputs.monocularVa },
  ].filter((p) => p.finding !== null) as Array<{
    key: CombinedLazyEyeRisk["driver"];
    finding: RiskFinding;
  }>;

  if (present.length === 0) return null;

  const vaInteroc = inputs.vaInteroc;
  const coverTest = inputs.coverTest;
  const phoria = inputs.phoria;

  const vaSupportsAmblyopia = (vaInteroc?.level ?? "low") === "high";

  // Pick the strongest finding (highest severity) as the natural driver.
  let driver: CombinedLazyEyeRisk["driver"] = present[0].key;
  let topSeverity = present[0].finding.severity;
  for (const p of present) {
    if (p.finding.severity > topSeverity) {
      topSeverity = p.finding.severity;
      driver = p.key;
    }
  }

  let level: RiskLevel = severityToLevel(topSeverity);

  // High-risk gating — see header comment.
  const coverHigh = coverTest?.level === "high";
  const supportingModerateOrHigh =
    coverTest?.level === "moderate" ||
    coverTest?.level === "high" ||
    phoria?.level === "moderate" ||
    phoria?.level === "high";
  const vaInterocModerate = vaInteroc?.level === "moderate";

  if (level === "high") {
    const allowedHigh =
      vaSupportsAmblyopia || coverHigh || (vaInterocModerate && supportingModerateOrHigh);
    if (!allowedHigh) level = "moderate";
  } else if (level === "moderate" && vaInterocModerate && supportingModerateOrHigh) {
    // Promotion: a one-line interocular gap *combined* with a separate
    // moderate-or-worse finding meets the "high risk" bar even if neither
    // signal alone does.
    level = "high";
  }

  // Critical-period nudge: surface moderate-band deviations a bit more
  // prominently for kids without claiming high risk.
  let severity = topSeverity;
  if (level === "low") {
    // A passing screen reads "0 out of 100" — no partial fills on the
    // risk ring when nothing crossed a threshold.
    severity = 0;
  } else if (level === "high") {
    severity = Math.max(severity, 0.7);
  } else if (level === "moderate" && isCriticalPeriod(band) && supportingModerateOrHigh) {
    severity = Math.max(severity, 0.55);
  }
  // Cap moderate percentage so the score stays visually distinct from high.
  if (level === "moderate") severity = Math.min(severity, 0.64);

  return {
    level,
    percentage: severityToPercentage(severity),
    driver,
    vaSupportsAmblyopia,
  };
}
